import React from 'react';
import type { CustomBriefExecutiveSummary, SiteRagReport } from '../../../../types/siteRag';
import { FindingClaimCard } from '../../report/FindingClaimCard';
import { FindingClaimsList } from '../../report/FindingClaimsList';
import { walkCitationText } from '../../../../lib/walkCitationText';
import { useEffectiveReportTheme } from '../../../../lib/useEffectiveReportTheme';
import { EditorialSubsection } from '../../report/editorial';
import { VerdictBadge } from './VerdictBadge';

interface Props {
  report: SiteRagReport;
  summary: CustomBriefExecutiveSummary;
}

/**
 * W2d editorial port — the hand-rolled uppercase-tracking `<h3>` chrome
 * ("Key findings" / "Recommended decision") becomes EditorialSubsection
 * (hairline rule + small-caps mono label, theme-scale adaptive). Content
 * is unchanged: VerdictBadge lead, FindingClaimCard per key finding, the
 * epistemic FindingClaimsList for material risks, and the cited
 * recommended-decision prose (walkCitationText preserved). Empty inputs
 * still drop their whole block.
 *
 * NOTE — this body is mounted INLINE by SiteRagReportView.tsx (custom-brief
 * special-case in the Executive Summary block), NOT via SectionsRouter.
 * Export name + props signature are load-bearing; do not change them.
 */
export const CustomBriefExecutiveSummaryBody: React.FC<Props> = ({ report, summary }) => {
  const theme = useEffectiveReportTheme();
  return (
    <div className="space-y-4" data-testid="custom-brief-executive-summary">
      <VerdictBadge verdict={summary.overallVerdict} />
      {summary.keyFindings.length > 0 && (
        <EditorialSubsection theme={theme} label="Key findings">
          <ul className="list-none space-y-2 pl-0">
            {summary.keyFindings.map((finding, i) => (
              <li key={i}>
                <FindingClaimCard claim={finding} references={report.references} />
              </li>
            ))}
          </ul>
        </EditorialSubsection>
      )}
      {summary.materialRisks.length > 0 && (
        <FindingClaimsList
          entries={summary.materialRisks}
          references={report.references}
          siteId={report.siteId}
          reportId={report.id}
          sectionId="customBriefExecutiveSummary"
          responses={report.openQuestionResponses}
          redFlagsHeading="Material risks (confirmed)"
          className="space-y-4"
        />
      )}
      {summary.recommendedDecision.trim() && (
        <EditorialSubsection theme={theme} label="Recommended decision">
          <p className="leading-relaxed">
            {walkCitationText(summary.recommendedDecision, report.references)}
          </p>
        </EditorialSubsection>
      )}
    </div>
  );
};
